import React, { useEffect, useMemo, useState } from 'react';
import { inventoryMetaApi } from '../../services/api';

function StatusPill({ status }) {
    return <span className={`status-pill${status === 'active' ? ' active' : ''}`}>{status}</span>;
}

function toFormState(item, fields) {
    const next = {};
    fields.forEach((field) => {
        if (field.type === 'tags') {
            next[field.name] = Array.isArray(field.getValue ? field.getValue(item) : item[field.name])
                ? (field.getValue ? field.getValue(item) : item[field.name]).join(', ')
                : '';
            return;
        }
        next[field.name] = field.getValue ? field.getValue(item) : item[field.name] ?? '';
    });
    next.status = item.status || 'active';
    return next;
}

export default function InventoryMetaPage({
    title,
    type,
    columns,
    fields,
    buildPayload,
}) {
    const [items, setItems] = useState([]);
    const [options, setOptions] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [modal, setModal] = useState({ open: false, item: null });
    const [form, setForm] = useState(() => ({ status: 'active' }));

    const load = async () => {
        setLoading(true);
        setError('');

        const [listRes, optionsRes] = await Promise.all([
            inventoryMetaApi.list(type),
            inventoryMetaApi.options(),
        ]);

        if (!listRes.success) {
            setError(listRes.error || 'Unable to load data');
            setLoading(false);
            return;
        }

        setItems(listRes.data || []);
        setOptions(optionsRes.success ? optionsRes.data || {} : {});
        setLoading(false);
    };

    useEffect(() => {
        const initialLoad = async () => {
            setLoading(true);
            setError('');

            const [listRes, optionsRes] = await Promise.all([
                inventoryMetaApi.list(type),
                inventoryMetaApi.options(),
            ]);

            if (!listRes.success) {
                setError(listRes.error || 'Unable to load data');
                setLoading(false);
                return;
            }

            setItems(listRes.data || []);
            setOptions(optionsRes.success ? optionsRes.data || {} : {});
            setLoading(false);
        };

        initialLoad();
    }, [type]);

    const filteredItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter((item) => {
            const matchesSearch = !q || Object.values(item).some((value) => String(value ?? '').toLowerCase().includes(q));
            const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [items, search, statusFilter]);

    const openCreate = () => {
        const empty = { status: 'active' };
        fields.forEach((field) => {
            empty[field.name] = field.defaultValue ?? '';
        });
        setForm(empty);
        setModal({ open: true, item: null });
    };

    const openEdit = (item) => {
        setForm(toFormState(item, fields));
        setModal({ open: true, item });
    };

    const closeModal = () => setModal({ open: false, item: null });

    const updateForm = (name, value) => setForm((current) => ({ ...current, [name]: value }));

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        const payload = buildPayload(form, options, modal.item);
        const response = modal.item
            ? await inventoryMetaApi.update(type, modal.item.id, payload)
            : await inventoryMetaApi.create(type, payload);
        setSaving(false);

        if (!response.success) {
            window.alert(response.error || 'Unable to save item');
            return;
        }

        closeModal();
        load();
    };

    const remove = async (item) => {
        if (!window.confirm(`Delete ${item.name}?`)) return;
        const response = await inventoryMetaApi.remove(type, item.id);
        if (!response.success) {
            window.alert(response.error || 'Unable to delete item');
            return;
        }
        load();
    };

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>{title}</h2>
                    <p>Dashboard <span className="crumb-sep">›</span> {title}</p>
                </div>
                <div className="products-toolbar-actions">
                    <button className="mini-icon-btn" type="button" onClick={load}>↻</button>
                    <button className="btn" type="button" onClick={openCreate}>Add {title.replace(/s$/, '')}</button>
                </div>
            </div>

            <section className="products-shell inventory-list-shell">
                <div className="products-filters">
                    <div className="products-search">
                        <span>⌕</span>
                        <input placeholder={`Search ${title.toLowerCase()}`} value={search} onChange={(event) => setSearch(event.target.value)} />
                    </div>
                    <div className="products-filter-right">
                        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                </div>

                {error ? <div className="error-banner">{error}</div> : null}

                <div className="products-table-wrap">
                    <table className="products-table admin-table">
                        <thead>
                            <tr>
                                {columns.map((column) => <th key={column.key}>{column.label}</th>)}
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={columns.length + 1} className="dash-empty-row">Loading...</td></tr>
                            ) : filteredItems.length === 0 ? (
                                <tr><td colSpan={columns.length + 1} className="dash-empty-row">No records found</td></tr>
                            ) : filteredItems.map((item) => (
                                <tr key={item.id}>
                                    {columns.map((column) => (
                                        <td key={column.key}>
                                            {column.render
                                                ? column.render(item)
                                                : column.key === 'status'
                                                    ? <StatusPill status={item.status} />
                                                    : item[column.key] ?? '-'}
                                        </td>
                                    ))}
                                    <td>
                                        <div className="product-actions">
                                            <button className="mini-icon-btn" type="button" onClick={() => openEdit(item)}>✎</button>
                                            <button className="mini-icon-btn" type="button" onClick={() => remove(item)}>🗑</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {modal.open ? (
                <div className="modal-backdrop" onClick={closeModal}>
                    <div className="modal-card" onClick={(event) => event.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                <h3>{modal.item ? `Edit ${title.replace(/s$/, '')}` : `Add ${title.replace(/s$/, '')}`}</h3>
                                <p>Manage live inventory metadata for this tenant.</p>
                            </div>
                            <button className="icon-btn" type="button" onClick={closeModal}>✕</button>
                        </div>
                        <form className="modal-form" onSubmit={submit}>
                            <div className="modal-grid modal-grid-2">
                                {fields.map((field) => {
                                    const optionList = field.options ? field.options(options, form) : [];
                                    if (field.type === 'textarea') {
                                        return (
                                            <label key={field.name} className="modal-field-span-2">
                                                <span>{field.label}</span>
                                                <textarea rows={4} value={form[field.name] || ''} onChange={(event) => updateForm(field.name, event.target.value)} />
                                            </label>
                                        );
                                    }
                                    if (field.type === 'image') {
                                        return (
                                            <label key={field.name} className="modal-field-span-2">
                                                <span>{field.label}</span>
                                                <input
                                                    value={form[field.name] || ''}
                                                    onChange={(event) => updateForm(field.name, event.target.value)}
                                                    placeholder={field.placeholder || 'https://...'}
                                                />
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(event) => {
                                                        const file = event.target.files?.[0];
                                                        if (!file) return;
                                                        const reader = new FileReader();
                                                        reader.onload = () => updateForm(field.name, String(reader.result || ''));
                                                        reader.readAsDataURL(file);
                                                    }}
                                                />
                                                {form[field.name] ? (
                                                    <img
                                                        src={form[field.name]}
                                                        alt={`${field.label} preview`}
                                                        style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--line)' }}
                                                    />
                                                ) : null}
                                                {field.helpText ? <small className="field-hint">{field.helpText}</small> : null}
                                            </label>
                                        );
                                    }
                                    if (field.type === 'select') {
                                        return (
                                            <label key={field.name}>
                                                <span>{field.label}</span>
                                                <select value={form[field.name] || ''} onChange={(event) => updateForm(field.name, event.target.value)}>
                                                    <option value="">Select {field.label}</option>
                                                    {optionList.map((option) => (
                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                </select>
                                            </label>
                                        );
                                    }
                                    return (
                                        <label key={field.name} className={field.type === 'tags' ? 'modal-field-span-2' : ''}>
                                            <span>{field.label}</span>
                                            <input
                                                value={form[field.name] || ''}
                                                onChange={(event) => updateForm(field.name, event.target.value)}
                                                placeholder={field.placeholder || ''}
                                            />
                                            {field.helpText ? <small className="field-hint">{field.helpText}</small> : null}
                                        </label>
                                    );
                                })}
                                <label>
                                    <span>Status</span>
                                    <select value={form.status || 'active'} onChange={(event) => updateForm('status', event.target.value)}>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </label>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="btn" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </div>
    );
}